// ═══════════════════════════════════════════════════════════════════════════════
// TRACE EXPORTER — Génération et import de traces de session
// Format JSON structuré, agnostique au référentiel
// ═══════════════════════════════════════════════════════════════════════════════

const SCHEMA_VERSION = "1.0";

// ── Génération d'identifiants ────────────────────────────────────────────────

const generateSessionId = () => {
  // Format court, lisible, unique : timestamp + random
  const ts = Date.now().toString(36);
  const rnd = Math.random().toString(36).slice(2, 10);
  return `psc_${ts}_${rnd}`;
};

// ── Horodatage ISO 8601 ──────────────────────────────────────────────────────

const nowISO = () => new Date().toISOString();

// ── Création d'une trace ─────────────────────────────────────────────────────

export const createTrace = (meta = {}) => {
  return {
    schema_version: SCHEMA_VERSION,

    // Session
    session_id: meta.session_id || generateSessionId(),
    actor_id: meta.actor_id || "anonymous",
    actor_label: meta.actor_label || null,
    timestamp_start: nowISO(),
    timestamp_end: null,
    referential: meta.referential || null,      // chaîne opaque
    profile: meta.profile || "apprenant",
    domain: meta.domain || null,
    notes: meta.notes || null,

    // Trajectoire
    position_initial: meta.position_initial || null,
    position_final: null,
    positions_history: [],
    trajectory_id: null,
    stages_reached: [],
    duration_seconds: null,
    scenarios_tested: [],

    // Preuve
    competency_ref: meta.competency_ref || null,
    level_achieved: null,
    observations: [],
    certification_ready: false,
    signature: null,
  };
};

// ── Ajout d'une position ─────────────────────────────────────────────────────

export const appendPosition = (trace, position) => {
  if (!trace || !position) return trace;

  const entry = {
    timestamp: nowISO(),
    position: { ...position },
  };

  return {
    ...trace,
    positions_history: [...(trace.positions_history || []), entry],
  };
};

// ── Ajout d'un résultat de scénario ──────────────────────────────────────────

export const appendScenarioResult = (trace, result) => {
  if (!trace || !result) return trace;

  const entry = {
    scenario_id: result.scenario_id,
    timestamp: nowISO(),
    posture_before: result.posture_before || null,
    posture_after: result.posture_after || null,
    chosen_behavior: result.chosen_behavior || null,
    outcome: result.outcome || "moderate",
    observations: result.observations || [],
  };

  return {
    ...trace,
    scenarios_tested: [...(trace.scenarios_tested || []), entry],
  };
};

// ── Ajout d'une observation ──────────────────────────────────────────────────

export const appendObservation = (trace, observation) => {
  if (!trace || !observation) return trace;

  return {
    ...trace,
    observations: [...(trace.observations || []), observation],
  };
};

// ── Marquage d'une étape de trajectoire ──────────────────────────────────────

export const markStageReached = (trace, stageName) => {
  if (!trace || !stageName) return trace;

  const stages = trace.stages_reached || [];
  if (stages.includes(stageName)) return trace;

  return {
    ...trace,
    stages_reached: [...stages, stageName],
  };
};

// ── Association d'une trajectoire ────────────────────────────────────────────

export const setTrajectory = (trace, trajectoryId) => {
  if (!trace) return trace;
  return { ...trace, trajectory_id: trajectoryId };
};

// ── Finalisation de la trace ─────────────────────────────────────────────────

export const finalizeTrace = (trace, finalPosition = null) => {
  if (!trace) return trace;

  const start = new Date(trace.timestamp_start).getTime();
  const end = Date.now();
  const duration = Math.round((end - start) / 1000);

  return {
    ...trace,
    timestamp_end: nowISO(),
    duration_seconds: duration,
    position_final: finalPosition || (trace.positions_history?.slice(-1)[0]?.position ?? null),
  };
};

// ── Preuve et certification ──────────────────────────────────────────────────

export const setEvidence = (trace, { competency_ref, level_achieved, certification_ready } = {}) => {
  if (!trace) return trace;

  return {
    ...trace,
    competency_ref: competency_ref ?? trace.competency_ref,
    level_achieved: level_achieved ?? trace.level_achieved,
    certification_ready: certification_ready ?? trace.certification_ready,
  };
};

// ── Signature légère (intégrité) ─────────────────────────────────────────────
// Note : ce n'est PAS une signature cryptographique forte.
// C'est un checksum qui détecte les modifications accidentelles.
// Pour de la signature réelle, il faudrait une clé privée et un algorithme dédié.

export const signTrace = (trace) => {
  if (!trace) return trace;

  const payload = JSON.stringify({
    session_id: trace.session_id,
    actor_id: trace.actor_id,
    timestamp_start: trace.timestamp_start,
    timestamp_end: trace.timestamp_end,
    position_initial: trace.position_initial,
    position_final: trace.position_final,
    scenarios_tested: trace.scenarios_tested,
    competency_ref: trace.competency_ref,
    level_achieved: trace.level_achieved,
  });

  // Simple hash FNV-1a 32-bit
  let hash = 0x811c9dc5;
  for (let i = 0; i < payload.length; i++) {
    hash ^= payload.charCodeAt(i);
    hash = (hash * 0x01000193) >>> 0;
  }

  return {
    ...trace,
    signature: hash.toString(16).padStart(8, "0"),
  };
};

// ── Export ───────────────────────────────────────────────────────────────────

export const exportTrace = (trace, { pretty = true } = {}) => {
  if (!trace) return null;
  return JSON.stringify(trace, null, pretty ? 2 : 0);
};

export const exportTraceAsBlob = (trace) => {
  const json = exportTrace(trace, { pretty: true });
  if (!json) return null;
  return new Blob([json], { type: "application/json" });
};

export const downloadTrace = (trace, filename = null) => {
  const blob = exportTraceAsBlob(trace);
  if (!blob) return false;

  const name = filename || `pesacore_trace_${trace.session_id}.json`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return true;
};

// ── Import ───────────────────────────────────────────────────────────────────

export const importTrace = (json) => {
  try {
    const parsed = typeof json === "string" ? JSON.parse(json) : json;
    if (!parsed || parsed.schema_version !== SCHEMA_VERSION) {
      console.warn("[traceExporter] Version de schéma non supportée :", parsed?.schema_version);
      return null;
    }
    return parsed;
  } catch (e) {
    console.error("[traceExporter] Erreur de parsing :", e);
    return null;
  }
};

// ── Vérification d'intégrité ─────────────────────────────────────────────────

export const verifyTrace = (trace) => {
  if (!trace || !trace.signature) return { valid: false, reason: "no_signature" };
  const reSigned = signTrace({ ...trace, signature: null });
  return {
    valid: reSigned.signature === trace.signature,
    reason: reSigned.signature === trace.signature ? "ok" : "signature_mismatch",
    expected: reSigned.signature,
    actual: trace.signature,
  };
};

// ── Résumé lisible ───────────────────────────────────────────────────────────

export const summarizeTrace = (trace) => {
  if (!trace) return null;

  const totalScenarios = trace.scenarios_tested?.length || 0;
  const successCount = trace.scenarios_tested?.filter(s => s.outcome === "success").length || 0;
  const failureCount = trace.scenarios_tested?.filter(s => s.outcome === "failure").length || 0;

  return {
    session_id: trace.session_id,
    actor: trace.actor_label || trace.actor_id,
    referential: trace.referential || "—",
    duration: trace.duration_seconds ? `${trace.duration_seconds}s` : "en cours",
    scenarios: { total: totalScenarios, success: successCount, failure: failureCount },
    level: trace.level_achieved || "non évalué",
    certification_ready: trace.certification_ready || false,
    signed: !!trace.signature,
  };
};